/**
 * Аккаунт: профиль, привязка брокера, депозит/вывод, настройки автоторговли,
 * рефералы. Визуал по design-spec §7 (Profile): hero → broker activation →
 * referral → utility. Наполнение — BOT_SPEC ШАГ 11–18, 21.
 */
import { useEffect, useState } from 'react';
import { ApiError, api, apiMessage } from '../lib/api.js';
import type { ReferralsPayload, SettingsPayload, StrategyPreset } from '../lib/api.js';
import { haptic, openLink, openTelegramLink } from '../lib/telegram.js';
import { money } from '../lib/format.js';
import { useCountUp } from '../lib/useCountUp.js';
import { useApp } from '../state/app.js';
import { SheetPortal } from '../components/SheetPortal.js';
import { InfoLinks, InfoSheet } from './InfoSheets.js';
import type { SheetId } from './InfoSheets.js';
import type { AppConfig } from '../lib/api.js';

export function AccountScreen({
  config,
}: {
  config: AppConfig | null;
}) {
  const { me, refresh, showToast, art, t } = useApp();
  const [sheet, setSheet] = useState<SheetId>(null);
  const [settings, setSettings] = useState<SettingsPayload | null>(null);
  const [referrals, setReferrals] = useState<ReferralsPayload | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  /**
   * Два явных пути, как в боте (замечание заказчика 2026-08-22): по умолчанию
   * «создать новый» — нам нужны регистрации под нами, а не только логины.
   */
  const [linkMode, setLinkMode] = useState<'create' | 'login'>('create');
  const [busy, setBusy] = useState(false);
  // выход из аккаунта: первый тап «взводит» кнопку, второй выполняет
  const [logoutArmed, setLogoutArmed] = useState(false);
  // тап по стратегии открывает шторку с описанием (тексты — из бота)
  const [strategySheet, setStrategySheet] = useState<StrategyPreset | null>(null);
  // «докручивающийся» реальный баланс
  const realBalance = useCountUp(me?.balances.real ?? 0);

  useEffect(() => {
    void api.settings().then(setSettings).catch(() => undefined);
    void api.referrals().then(setReferrals).catch(() => undefined);
  }, [me?.broker.linked]);

  async function linkAccount() {
    haptic('medium');
    setBusy(true);
    try {
      const result = await api.linkAccount(email);
      if (result.status === 'created') {
        showToast(t('app.account.created', { password: result.password ?? '' }));
        await refresh();
      } else if (result.status === 'exists') {
        // почта занята — переключаем на вход, чтобы человек ввёл пароль
        setLinkMode('login');
        showToast(t('app.account.found'));
      } else {
        showToast(t('app.account.conflict'));
      }
    } catch (err) {
      showToast(
        err instanceof ApiError && err.isConflict
          ? t('app.account.conflict')
          : err instanceof Error
            ? err.message
            : t('app.account.link_failed'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function loginAccount() {
    setBusy(true);
    try {
      const result = await api.loginAccount(email, password);
      if (result.status === 'linked') {
        showToast(t('app.account.connected'));
        setLinkMode('create');
        await refresh();
      } else if (result.status === '2fa') {
        showToast(t('app.account.need_2fa'));
      } else {
        showToast(t('app.account.wrong_password'));
      }
    } catch (err) {
      // пароль верный, но счёт уже привязан к другому Telegram — 409 от сервера;
      // без этой ветки сбой брокера выглядел как «ничего не произошло»
      showToast(
        err instanceof ApiError && err.isConflict
          ? t('app.account.conflict')
          : apiMessage(err, t, 'app.account.link_failed'),
      );
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!logoutArmed) return;
    const timer = setTimeout(() => setLogoutArmed(false), 4_000);
    return () => clearTimeout(timer);
  }, [logoutArmed]);

  async function logoutAccount() {
    haptic('medium');
    if (!logoutArmed) {
      setLogoutArmed(true);
      return;
    }

    setLogoutArmed(false);
    setBusy(true);
    try {
      await api.unlinkAccount();
      // поля формы очищаем: дальше сюда можно ввести другую почту,
      // и снова предлагаем СОЗДАТЬ новый (а не только войти)
      setEmail('');
      setPassword('');
      setLinkMode('create');
      showToast(t('app.account.logged_out'));
      await refresh();
    } catch (err) {
      showToast(apiMessage(err, t, 'app.account.save_failed'));
    } finally {
      setBusy(false);
    }
  }

  async function toggleMode() {
    haptic('medium');
    const goingReal = me?.user.mode !== 'REAL';
    setBusy(true);
    try {
      await api.setMode(goingReal ? 'REAL' : 'DEMO');
      await refresh();
      showToast(t(goingReal ? 'app.account.real_on' : 'app.account.demo_on'));
    } catch (err) {
      // известные коды (депозит, чужой аккаунт) переводятся словарём
      showToast(apiMessage(err, t, 'app.account.save_failed'));
    } finally {
      setBusy(false);
    }
  }

  async function openBrokerLink(kind: 'deposit' | 'withdraw' | 'platform') {
    haptic('medium');
    try {
      const { link } =
        kind === 'deposit'
          ? await api.depositLink()
          : kind === 'withdraw'
            ? await api.withdrawalLink()
            : await api.platformLink();
      openLink(link);
    } catch (err) {
      showToast(apiMessage(err, t, 'app.tokens.broker_silent'));
    }
  }

  async function patchSettings(patch: Parameters<typeof api.updateSettings>[0], toast: string) {
    haptic();
    try {
      await api.updateSettings(patch);
      setSettings(await api.settings());
      await refresh();
      showToast(toast);
    } catch (err) {
      // в REAL стратегия «от $X» требует реального баланса — сумма важнее
      // безликого «не сохранилось»
      const minBalance =
        err instanceof ApiError
          ? (err.payload as { strategyMinBalance?: number } | null)?.strategyMinBalance
          : undefined;
      showToast(
        typeof minBalance === 'number'
          ? t('app.account.strategy_locked', { amount: minBalance })
          : t('app.account.save_failed'),
      );
    }
  }

  if (!me) return null;

  return (
    <div className="screen">
      {/* профиль и балансы */}
      <section className="card card-lg">
        <div className="row-between">
          <div>
            <span className="kicker">{t('app.nav.account')}</span>
            <div className="profile-name">{me.user.email ?? t('app.account.no_link')}</div>
          </div>
          <span className={`pill ${me.user.mode === 'REAL' ? 'pill-green' : 'pill-pink'}`}>
            {me.user.mode}
          </span>
        </div>

        {/* trader_id — то, по чему поддержка и партнёрка находят человека
            у брокера; без него на вопрос «какой у меня счёт» ответить нечем */}
        {me.broker.traderId && (
          <div className="row-between">
            <span className="label">{t('app.account.trader_id')}</span>
            <button
              type="button"
              className="pill"
              onClick={() => {
                void navigator.clipboard?.writeText(me.broker.traderId ?? '');
                showToast(t('app.account.trader_id_copied'));
              }}
            >
              {me.broker.traderId}
            </button>
          </div>
        )}

        {/* счёт живёт на настоящей платформе — даём открыть кабинет брокера
            (просьба заказчика 2026-08-21); ссылка через /api/go/platform */}
        {me.broker.linked && (
          <div className="row-between">
            <span className="label">{t('app.account.platform')}</span>
            <button
              type="button"
              className="pill"
              onClick={() => void openBrokerLink('platform')}
            >
              {t('app.account.platform_open')} ↗
            </button>
          </div>
        )}

        <div className="label">{t('app.account.balance')}</div>
        <div className="profile-balance">
          <span className="profile-balance-sign">$</span>
          {money(realBalance)}
          <span className="profile-balance-currency">{me.balances.currency}</span>
        </div>
        <div className="body">
          {t('app.account.demo_line', { demo: money(me.balances.demo), tokens: me.user.tokens })}
        </div>

        <div className="row">
          <button
            type="button"
            className="cta grow"
            disabled={!me.broker.linked}
            onClick={() => void openBrokerLink('deposit')}
          >
            {t('app.home.deposit')}
          </button>
          <button
            type="button"
            className="cta cta-secondary grow"
            disabled={!me.broker.linked}
            onClick={() => void openBrokerLink('withdraw')}
          >
            {t('app.account.withdraw')}
          </button>
        </div>

        {/* второй ряд действий — те же половины, что Deposit/Withdraw выше
            (аудит UI 2026-08-21, A1): раньше кнопки шли по ширине текста.
            «Real» ведёт через гейт (депозит/чужой аккаунт), «выход» — с
            двухшаговым подтверждением; сам аккаунт у брокера остаётся */}
        {me.broker.linked && (
          <div className="row">
            <button
              type="button"
              className="cta cta-secondary cta-sm grow"
              disabled={busy}
              onClick={() => void toggleMode()}
            >
              {t(me.user.mode === 'REAL' ? 'app.account.go_demo' : 'app.account.go_real')}
            </button>
            <button
              type="button"
              className="cta cta-secondary cta-sm grow"
              disabled={busy}
              onClick={() => void logoutAccount()}
            >
              {logoutArmed ? t('app.account.logout_confirm') : t('app.account.logout')}
            </button>
          </div>
        )}
      </section>

      {/* привязка брокера */}
      {!me.broker.linked && (
        <section className="card card-lg card-violet">
          <img className="broker-art" src={art('art.broker', '/art/broker.webp')} alt="" width={132} height={132} />
          <span className="kicker">{t('app.account.activation')}</span>
          <h2 className="card-title">{t('app.account.connect_title')}</h2>
          <p className="body">
            {t('app.account.connect_text')}
          </p>
          {/* два явных пути, как в боте: создать новый (приоритет) или войти */}
          <div className="chip-grid" style={{ ['--chip-cols' as string]: 2, marginBottom: 10 }}>
            <button
              type="button"
              className={`chip ${linkMode === 'create' ? 'is-active' : ''}`}
              onClick={() => setLinkMode('create')}
            >
              <span className="chip-label">{t('app.account.tab_create')}</span>
            </button>
            <button
              type="button"
              className={`chip ${linkMode === 'login' ? 'is-active' : ''}`}
              onClick={() => setLinkMode('login')}
            >
              <span className="chip-label">{t('app.account.tab_login')}</span>
            </button>
          </div>

          <input
            className="field"
            type="email"
            inputMode="email"
            placeholder="name@mail.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          {linkMode === 'login' && (
            <input
              className="field"
              type="password"
              placeholder={t('app.account.password_placeholder')}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
          <button
            type="button"
            className="cta cta-block"
            disabled={busy || email.length < 5 || (linkMode === 'login' && password.length === 0)}
            onClick={() => void (linkMode === 'login' ? loginAccount() : linkAccount())}
          >
            {busy
              ? t('app.account.checking')
              : linkMode === 'login'
                ? t('app.account.login')
                : t('app.account.create_button')}
          </button>
        </section>
      )}

      {/* настройки автоторговли */}
      {settings && (
        <section className="card card-lg">
          <span className="kicker">{t('app.account.autotrade')}</span>
          <h2 className="card-title">{t('app.account.strategy')}</h2>

          <div className="chip-grid">
            {settings.options.strategies
              .filter((preset) => !preset.manual)
              .map((preset) => {
                // в REAL порог «от $X» обязателен: реальный мартингейл с меньшим
                // балансом оборвётся на середине лестницы (сервер тоже проверяет)
                const locked =
                  me.user.mode === 'REAL' &&
                  preset.minBalanceUsd !== null &&
                  me.balances.real < preset.minBalanceUsd;

                // без эмодзи и с витринным именем; тап открывает шторку с
                // полным описанием — применение только после подтверждения
                return (
                  <button
                    key={preset.id}
                    type="button"
                    disabled={locked}
                    style={locked ? { opacity: 0.45 } : undefined}
                    className={`chip chip-strategy ${settings.current.strategy === preset.id ? 'is-active' : ''}`}
                    onClick={() => {
                      haptic();
                      setStrategySheet(preset);
                    }}
                  >
                    <span className="chip-label">
                      {preset.label}
                      {locked ? ` · $${preset.minBalanceUsd}+` : ''}
                    </span>
                  </button>
                );
              })}
          </div>

          <div className="label">{t('app.account.expiration')}</div>
          <div className="chip-grid" style={{ '--chip-cols': 3 } as React.CSSProperties}>
            {settings.options.expirations.map((seconds) => (
              <button
                key={seconds}
                type="button"
                className={`chip ${settings.current.expirationSeconds === seconds ? 'is-active' : ''}`}
                onClick={() =>
                  void patchSettings({ expirationSeconds: seconds }, t('app.account.expiration_set', { seconds }))
                }
              >
                {t('app.account.seconds', { seconds })}
              </button>
            ))}
          </div>

          <div className="row-between">
            <div>
              <div className="label">{t('app.account.mode')}</div>
              <div className="body">
                {settings.current.autotrade ? t('app.account.mode_auto') : t('app.account.mode_manual')}
              </div>
            </div>
            <button
              type="button"
              className="cta cta-secondary cta-sm"
              onClick={() =>
                void patchSettings(
                  { autotrade: !settings.current.autotrade },
                  settings.current.autotrade
                    ? t('app.account.mode_manual_on')
                    : t('app.account.mode_auto_on'),
                )
              }
            >
              {t('app.account.switch')}
            </button>
          </div>
        </section>
      )}

      {/* рефералы */}
      {referrals && (
        <section className="card card-lg card-tinted">
          <img className="referral-art" src={art('art.referral', '/art/referral.webp')} alt="" loading="lazy" />
          <span className="kicker">{t('app.account.referral')}</span>
          <h2 className="card-title">{t('app.account.referral_title')}</h2>

          <div className="stat-grid">
            <div className="stat-tile">
              {/* именно total: друг, начавший торговать, меняет статус и
                  из счётчика invited пропал бы */}
              <div className="stat-value">{referrals.stats.total}</div>
              <div className="stat-label">{t('app.account.invited')}</div>
            </div>
            <div className="stat-tile">
              <div className="stat-value" style={{ '--stat-color': 'var(--green)' } as React.CSSProperties}>
                {referrals.stats.confirmed}
              </div>
              <div className="stat-label">{t('app.account.confirmed')}</div>
            </div>
            <div className="stat-tile">
              <div className="stat-value" style={{ '--stat-color': 'var(--gold)' } as React.CSSProperties}>
                {referrals.stats.tokensEarned}
              </div>
              <div className="stat-label">{t('app.home.stat_tokens')}</div>
            </div>
          </div>

          <div className="referral-link">{referrals.link}</div>
          <button
            type="button"
            className="cta cta-block"
            onClick={() => {
              void navigator.clipboard?.writeText(referrals.link);
              showToast(t('app.account.link_copied'));
            }}
          >
            {t('app.account.copy_link')}
          </button>
        </section>
      )}

      <InfoLinks onOpen={setSheet} />

      {/* текст «напишите @…» убран по просьбе заказчика 2026-08-22 — осталась
          только кнопка; чат открывается поверх прилы (openTelegramLink) */}
      {config?.links?.support && (
        <section className="card">
          <span className="label">{t('app.account.support')}</span>
          <button
            type="button"
            className="cta cta-block"
            onClick={() => {
              haptic();
              openTelegramLink(config.links!.support!);
            }}
          >
            {t('app.community.support')}
          </button>
        </section>
      )}

      <InfoSheet id={sheet} config={config} onClose={() => setSheet(null)} />

      {/* описание стратегии перед применением — тот же текст, что в боте */}
      {strategySheet && (
        <SheetPortal>
        <div
          className="sheet-backdrop"
          onClick={(event) => {
            if (event.target === event.currentTarget) setStrategySheet(null);
          }}
        >
          <div className="sheet">
            <div className="sheet-handle" />
            <span className="kicker">{t('app.account.autotrade')}</span>
            <h2 className="strategy-sheet-title">{strategySheet.label}</h2>
            {/* механика серии: чем многосделочные режимы отличаются от классики */}
            <p className="body">{t('app.strategy.mode.' + (strategySheet.mode ?? 'recovery'))}</p>

            {/* уровень риска = позиция пресета в линейке, от PULSE к APEX */}
            {(() => {
              const presets = (settings?.options.strategies ?? []).filter((p) => !p.manual);
              const level = presets.findIndex((p) => p.id === strategySheet.id) + 1;
              return (
                <div className="risk-meter">
                  <span className="risk-label">{t('app.strategy.risk')}</span>
                  <div className="risk-meter-cells">
                    {presets.map((p, index) => (
                      <span key={p.id} className={`risk-cell ${index < level ? 'is-on' : ''}`} />
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="strategy-specs">
              <div className="stat-tile">
                <div className="stat-value">×{strategySheet.progression}</div>
                <div className="stat-label">{t('app.strategy.progression')}</div>
              </div>
              <div className="stat-tile">
                <div className="stat-value">{strategySheet.maxSteps}</div>
                <div className="stat-label">{t('app.strategy.steps')}</div>
              </div>
              <div className="stat-tile">
                <div className="stat-value">{strategySheet.maxTrades}</div>
                <div className="stat-label">{t('app.strategy.trades')}</div>
              </div>
              <div className="stat-tile">
                <div
                  className="stat-value"
                  style={{ '--stat-color': 'var(--gold)' } as React.CSSProperties}
                >
                  {strategySheet.minBalanceUsd === null ? '—' : `$${strategySheet.minBalanceUsd}`}
                </div>
                <div className="stat-label">{t('app.strategy.min_balance')}</div>
              </div>
            </div>

            <div className="row" style={{ marginTop: 16 }}>
              <button
                type="button"
                className="cta grow"
                onClick={() => {
                  const preset = strategySheet;
                  setStrategySheet(null);
                  void patchSettings(
                    { strategy: preset.id },
                    t('app.account.strategy_set', { strategy: preset.label }),
                  );
                }}
              >
                {t('app.strategy.apply')}
              </button>
              <button
                type="button"
                className="cta cta-secondary grow"
                onClick={() => setStrategySheet(null)}
              >
                {t('app.common.back')}
              </button>
            </div>
          </div>
        </div>
        </SheetPortal>
      )}
    </div>
  );
}
